import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Artist } from './types';
import { artistsApi } from '../../repositories/artists';
import PhotoUpload from './PhotoUpload';
import Swal from 'sweetalert2';
import MultiSelect from '../../PitchFormPage/components/MultiSelect/MultiSelect';
import Button from '../ui/Button';
import { dspDropdownOptions } from '../../PitchFormPage/Data/dropdownOptions';
interface ArtistDetailsTabProps {
  artist: Artist;
  onUpdate: (updatedArtist: Artist) => void;
}

// Types
export interface UpdateArtistFormValues {
  artistName: string;
  spotifyURI: string;
  appleID: string;
  asin: string;
  soundcloudURL: string;
  tidalURL: string;
  bio: string;
  hometown: string;
  currentTown: string;
  country: string;
  label: string;
  distributor: string;
  instagram: string;
  tiktok: string;
  twitter: string;
  facebook: string;
  snapchat: string;
  spotifyURL: string;
  appleURL: string;
  amazonURL: string;
  deezerURL: string;
  listenersLocation: string;
  lyricLanguage: string;
  vevoRep: string;
  vevoRepOther: string;
  soundCloudContact: string;
  similarArtists: string;
}

// Validation Schema - same format rules as AddArtist. Which of AddArtist's required fields
// are required here depends on the artist: see buildUpdateArtistValidationSchema.
const updateArtistValidationSchema = Yup.object().shape({
  artistName: Yup.string()
    .min(2, 'Artist name must be at least 2 characters')
    .max(255, 'Artist name must be less than 255 characters'),

  spotifyURI: Yup.string()
    .matches(
      /^[a-zA-Z0-9]{22}$/,
      'Invalid Spotify URI format. Should be 22 characters'
    ),

  appleID: Yup.string()
    .matches(/^\d+$/, 'Apple ID must contain only numbers'),

  asin: Yup.string(),

  soundcloudURL: Yup.string()
    .url('Invalid SoundCloud URL format'),

  tidalURL: Yup.string()
    .url('Invalid TIDAL URL format'),

  // Optional fields
  bio: Yup.string()
    .max(5000, 'Bio must be less than 5000 characters'),

  hometown: Yup.string()
    .max(100, 'Hometown must be less than 100 characters'),

  currentTown: Yup.string()
    .max(100, 'Current town must be less than 100 characters'),

  country: Yup.string()
    .max(100, 'Country must be less than 100 characters'),

  label: Yup.string()
    .max(100, 'Label must be less than 100 characters'),

  distributor: Yup.string()
    .max(100, 'Distributor must be less than 100 characters'),

  // Social Media URLs
  instagram: Yup.string()
    .url('Invalid Instagram URL format')
    .nullable(),

  tiktok: Yup.string()
    .url('Invalid TikTok URL format')
    .nullable(),

  twitter: Yup.string()
    .url('Invalid Twitter URL format')
    .nullable(),

  facebook: Yup.string()
    .url('Invalid Facebook URL format')
    .nullable(),

  snapchat: Yup.string()
    .url('Invalid Snapchat URL format')
    .nullable(),

  // Streaming platform URLs
  spotifyURL: Yup.string()
    .url('Invalid Spotify URL format')
    .nullable(),

  appleURL: Yup.string()
    .url('Invalid Apple Music URL format')
    .nullable(),

  amazonURL: Yup.string()
    .url('Invalid Amazon URL format')
    .nullable(),

  deezerURL: Yup.string()
    .url('Invalid Deezer URL format')
    .nullable(),
  listenersLocation: Yup.string().nullable()
});

/**
 * AddArtist's required fields, with AddArtist's messages. (Spotify URI is required there
 * too, but it can't be edited here.)
 */
const REQUIRED_WHEN_SET = {
  artistName: 'Artist name is required',
  appleID: 'Apple ID is required',
  asin: 'ASIN is required',
  soundcloudURL: 'SoundCloud URL is required',
  tidalURL: 'TIDAL URL is required',
} as const;

/**
 * A field that is required on create can't be cleared on edit — but only if the artist
 * actually has a value for it. Artists created before a field became required may have it
 * blank, and they must stay editable without being forced to fill it in first.
 */
export const buildUpdateArtistValidationSchema = (initial: UpdateArtistFormValues) =>
  updateArtistValidationSchema.shape(
    Object.fromEntries(
      (Object.keys(REQUIRED_WHEN_SET) as (keyof typeof REQUIRED_WHEN_SET)[])
        .filter((key) => initial[key].trim() !== '')
        .map((key) => [
          key,
          (updateArtistValidationSchema.fields[key] as Yup.StringSchema).required(
            REQUIRED_WHEN_SET[key]
          ),
        ])
    )
  );

const ArtistDetailsTab: React.FC<ArtistDetailsTabProps> = ({ artist, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>(artist.photo || '');
  // Set when the user picks an existing asset instead of uploading a file. On submit its URL
  // is fetched into a File so the backend's file-based `photo` field stays unchanged.
  const [photoAssetUrl, setPhotoAssetUrl] = useState<string | null>(null);

  // Initial form values from artist data
  const initialValues: UpdateArtistFormValues = {
    artistName: artist.artistName || '',
    spotifyURI: artist.spotifyURI || '',
    appleID: artist.appleID || '',
    asin: artist.asin || '',
    soundcloudURL: artist.soundcloudURL || '',
    tidalURL: artist.tidalURL || '',
    bio: artist.bio || '',
    hometown: artist.hometown || '',
    currentTown: artist.currentTown || '',
    country: artist.country || '',
    label: artist.label || '',
    distributor: artist.distributor || '',
    instagram: artist.instagram || '',
    tiktok: artist.tiktok || '',
    twitter: artist.twitter || '',
    facebook: artist.facebook || '',
    snapchat: artist.snapchat || '',
    spotifyURL: artist.spotifyURL || '',
    appleURL: artist.appleURL || '',
    amazonURL: artist.amazonURL || '',
    deezerURL: artist.deezerURL || '',
    listenersLocation: artist.listenersLocation || '',
    lyricLanguage: artist.lyricLanguage || '',
    vevoRep: artist.vevoRep || '',
    vevoRepOther: artist.vevoRepOther || '',
    soundCloudContact: artist.soundCloudContact || '',
    similarArtists: artist.similarArtists || '',
  };

  const validationSchema = buildUpdateArtistValidationSchema(initialValues);



  const handlePhotoSelect = (file: File) => {
    setPhotoAssetUrl(null);
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPhotoPreview(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleAssetSelect = (url: string) => {
    setPhotoFile(null);
    setPhotoAssetUrl(url);
    setPhotoPreview(url);
  };

  const handleSubmit = async (values: UpdateArtistFormValues) => {
    try {
      setLoading(true);

      // Prepare update data with backend field names
      const updateData = new FormData();

      // Add photo if selected. A picked asset is sent as its URL; the backend checks it
      // belongs to this artist and uses it directly (fetching it here hits S3 CORS).
      if (photoFile) {
        updateData.append('photo', photoFile);
      } else if (photoAssetUrl) {
        updateData.append('photoAssetUrl', photoAssetUrl);
      }

      // Required fields - use backend field names
      updateData.append('artistName', values.artistName.trim());
      updateData.append('spotifyURI', values.spotifyURI.trim());
      updateData.append('appleID', values.appleID.trim());
      updateData.append('asin', values.asin.trim());
      updateData.append('soundcloudURL', values.soundcloudURL.trim());
      updateData.append('tidalURL', values.tidalURL.trim());

      // Optional fields - only append if they have values
      if (values.bio.trim()) updateData.append('bio', values.bio.trim());
      if (values.hometown.trim()) updateData.append('hometown', values.hometown.trim());
      if (values.currentTown.trim()) updateData.append('currentTown', values.currentTown.trim());
      if (values.country.trim()) updateData.append('country', values.country.trim());
      if (values.label.trim()) updateData.append('label', values.label.trim());
      if (values.distributor.trim()) updateData.append('distributor', values.distributor.trim());
      if (values.instagram.trim()) updateData.append('instagram', values.instagram.trim());
      if (values.tiktok.trim()) updateData.append('tiktok', values.tiktok.trim());
      if (values.twitter.trim()) updateData.append('twitter', values.twitter.trim());
      if (values.facebook.trim()) updateData.append('facebook', values.facebook.trim());
      if (values.snapchat.trim()) updateData.append('snapchat', values.snapchat.trim());
      if (values.spotifyURL.trim()) updateData.append('spotifyURL', values.spotifyURL.trim());
      if (values.appleURL.trim()) updateData.append('appleURL', values.appleURL.trim());
      if (values.amazonURL.trim()) updateData.append('amazonURL', values.amazonURL.trim());
      if (values.deezerURL.trim()) updateData.append('deezerURL', values.deezerURL.trim());
      if (values.listenersLocation.trim()) updateData.append('listenersLocation', values.listenersLocation.trim());
      if (values.lyricLanguage.trim()) updateData.append('lyricLanguage', values.lyricLanguage.trim());
      if (values.vevoRep.trim()) updateData.append('vevoRep', values.vevoRep.trim());
      if (values.vevoRepOther.trim()) updateData.append('vevoRepOther', values.vevoRepOther.trim());
      if (values.soundCloudContact.trim()) updateData.append('soundCloudContact', values.soundCloudContact.trim());
      if (values.similarArtists.trim()) updateData.append('similarArtists', values.similarArtists.trim());

      const response = await artistsApi.update(artist.id, updateData);
      const updatedArtistData = response.data.data || response.data;

      // Create updated artist with backend field names
      const mappedArtist: Artist = {
        ...artist,
        id: updatedArtistData.id,
        artistName: updatedArtistData.artistName,
        photo: updatedArtistData.photo,
        spotifyURI: updatedArtistData.spotifyURI,
        appleID: updatedArtistData.appleID,
        asin: updatedArtistData.asin,
        soundcloudURL: updatedArtistData.soundcloudURL,
        tidalURL: updatedArtistData.tidalURL,
        bio: updatedArtistData.bio,
        hometown: updatedArtistData.hometown,
        currentTown: updatedArtistData.currentTown,
        country: updatedArtistData.country,
        label: updatedArtistData.label,
        distributor: updatedArtistData.distributor,
        instagram: updatedArtistData.instagram,
        tiktok: updatedArtistData.tiktok,
        twitter: updatedArtistData.twitter,
        facebook: updatedArtistData.facebook,
        snapchat: updatedArtistData.snapchat,
        spotifyURL: updatedArtistData.spotifyURL,
        appleURL: updatedArtistData.appleURL,
        amazonURL: updatedArtistData.amazonURL,
        deezerURL: updatedArtistData.deezerURL,
        listenersLocation: updatedArtistData.listenersLocation,
        lyricLanguage: updatedArtistData.lyricLanguage,
        vevoRep: updatedArtistData.vevoRep,
        vevoRepOther: updatedArtistData.vevoRepOther,
        soundCloudContact: updatedArtistData.soundCloudContact,
        similarArtists: updatedArtistData.similarArtists,
        userId: updatedArtistData.userId || artist.userId,
        pitchCount: updatedArtistData.pitchCount || artist.pitchCount,
        createdAt: new Date(updatedArtistData.createdAt),
        updatedAt: new Date(updatedArtistData.updatedAt),
      };

      onUpdate(mappedArtist);
      setIsEditing(false);
      setPhotoFile(null);
      setPhotoAssetUrl(null);

      Swal.fire({
        title: 'Success!',
        text: 'Artist details updated successfully.',
        icon: 'success',
        confirmButtonText: 'OK'
      });

    } catch (error: any) {
      console.error('Error updating artist:', error);

      if (error.response?.status === 400 && error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;

        let errorMessage = "Validation failed:\n";
        validationErrors.forEach((err: any, index: number) => {
          errorMessage += `• ${err}\n`;
        });

        Swal.fire({
          title: 'Validation Error!',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      } else {
        // Handle other errors
        const errorMessage = error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          'Failed to update artist details. Please try again.';

        Swal.fire({
          title: 'Error!',
          text: errorMessage,
          icon: 'error',
          confirmButtonText: 'OK'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setPhotoPreview(artist.photo || '');
    setPhotoFile(null);
    setPhotoAssetUrl(null);
    setIsEditing(false);
  };

  // Custom input component with error handling - EXACT same as AddArtist
  const InputField = ({
    name,
    label,
    placeholder,
    type = 'text',
    required = false,
    maxLength,
    rows,
    disabled = false
  }: {
    name: string;
    label: string;
    placeholder: string;
    type?: string;
    required?: boolean;
    maxLength?: number;
    rows?: number;
    disabled?: boolean;
  }) => (
    <div className="space-y-2">
      <label htmlFor={name} className="block text-sm font-medium text-neutral-700">
        {label} {required && <span className="text-danger-500">*</span>}
      </label>
      <Field name={name}>
        {({ field, form, meta }: any) => (
          <>
            {type === 'textarea' ? (
              <textarea
                {...field}
                id={name}
                placeholder={placeholder}
                maxLength={maxLength}
                rows={rows}
                disabled={disabled}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:border-primary-500 disabled:bg-neutral-100 disabled:text-neutral-400 disabled:cursor-not-allowed ${meta.touched && meta.error ? 'border-danger-500' : 'border-neutral-300'
                  }`}
                onChange={(e) => {
                  // Special handling for Apple ID - only numbers
                  if (name === 'appleID') {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    form.setFieldValue(name, numericValue);
                    return;
                  }

                  // Special handling for Spotify URI - clean format
                  if (name === 'spotifyURI') {
                    let cleanValue = e.target.value;
                    if (cleanValue.includes('spotify:artist:')) {
                      cleanValue = cleanValue.replace('spotify:artist:', '');
                    }
                    form.setFieldValue(name, cleanValue);
                    return;
                  }

                  field.onChange(e);
                }}
              />
            ) : (
              <input
                {...field}
                type={type}
                id={name}
                placeholder={placeholder}
                maxLength={maxLength}
                disabled={disabled}
                className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:border-primary-500 disabled:bg-neutral-100 disabled:text-neutral-400 disabled:cursor-not-allowed ${meta.touched && meta.error ? 'border-danger-500' : 'border-neutral-300'
                  }`}
                onChange={(e) => {
                  // Special handling for Apple ID - only numbers
                  if (name === 'appleID') {
                    const numericValue = e.target.value.replace(/[^0-9]/g, '');
                    form.setFieldValue(name, numericValue);
                    return;
                  }

                  // Special handling for Spotify URI - clean format
                  if (name === 'spotifyURI') {
                    let cleanValue = e.target.value;
                    if (cleanValue.includes('spotify:artist:')) {
                      cleanValue = cleanValue.replace('spotify:artist:', '');
                    }
                    form.setFieldValue(name, cleanValue);
                    return;
                  }

                  field.onChange(e);
                }}
              />
            )}
            {/* Character counter for bio */}
            {name === 'bio' && (
              <div className="text-sm text-neutral-500">
                {field.value.length}/5000 characters
              </div>
            )}
            <ErrorMessage name={name} component="div" className="text-sm text-danger-500" />
          </>
        )}
      </Field>
    </div>
  );

  if (!isEditing) {
    return (
      <div className="space-y-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
          {/* Left Column */}
          <div className="space-y-6 min-w-0">
            {/* Artist Photo and Name */}
            <div className="text-center">
              <div className="w-32 h-32 mx-auto mb-4 overflow-hidden bg-neutral-100 rounded-lg">
                {artist.photo ? (
                  <img
                    src={artist.photo}
                    alt={artist.artistName}
                    className="object-cover w-full h-full"
                  />
                ) : (
                  <div className="flex items-center justify-center w-full h-full">
                    <svg
                      className="w-16 h-16 text-neutral-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                      />
                    </svg>
                  </div>
                )}
              </div>
              <h2 className="text-2xl font-bold text-black">{artist.artistName}</h2>
            </div>

            {/* Bio */}
            {artist.bio && (
              <div>
                <h3 className="mb-2 text-lg font-medium text-black">Bio</h3>
                <p className="text-neutral-700">{artist.bio}</p>
              </div>
            )}

            {/* Platform IDs */}
            <div>
              <h3 className="mb-2 text-lg font-medium text-black">Platform IDs</h3>
              <div className="space-y-1 text-sm text-neutral-600">
                <div>
                  <span className="font-medium">Spotify URI:</span> {artist.spotifyURI}
                </div>
                <div>
                  <span className="font-medium">Apple ID:</span> {artist.appleID}
                </div>
                <div>
                  <span className="font-medium">ASIN:</span> {artist.asin}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6 min-w-0">
            {/* Social Media */}
            <div>
              <h3 className="mb-3 text-lg font-medium text-black">Social Media</h3>
              <div className="space-y-1 text-sm text-neutral-600">
                {artist.instagram && <div><span className="font-medium">Instagram:</span> {artist.instagram}</div>}
                {artist.tiktok && <div><span className="font-medium">TikTok:</span> {artist.tiktok}</div>}
                {artist.twitter && <div><span className="font-medium">X:</span> {artist.twitter}</div>}
                {artist.facebook && <div><span className="font-medium">Facebook:</span> {artist.facebook}</div>}
                {artist.snapchat && <div><span className="font-medium">Snapchat:</span> {artist.snapchat}</div>}
                {!artist.instagram && !artist.tiktok && !artist.twitter && !artist.facebook && !artist.snapchat && (
                  <div className="text-neutral-400">No social media links provided</div>
                )}
              </div>
            </div>

            {/* Location */}
            <div>
              <h3 className="mb-3 text-lg font-medium text-black">Location</h3>
              <div className="text-sm text-neutral-600">
                {(artist.hometown || artist.currentTown || artist.country || artist.listenersLocation || artist.lyricLanguage) ? (
                  <div>
                    {artist.hometown && <><span className="font-medium">Hometown:</span> {artist.hometown}</>}
                    {artist.hometown && artist.currentTown && ' | '}
                    {artist.currentTown && <><span className="font-medium">Current:</span> {artist.currentTown}</>}
                    {artist.country && <div><span className="font-medium">Country:</span> {artist.country}</div>}
                    {artist.listenersLocation && <div><span className="font-medium">Listeners Location:</span> {artist.listenersLocation}</div>}
                    {artist.lyricLanguage && <div><span className="font-medium">Lyric Language:</span> {artist.lyricLanguage}</div>}
                    {artist.similarArtists && <div><span className="font-medium">Similar Artists:</span> {artist.similarArtists}</div>}
                  </div>
                ) : (
                  <div className="text-neutral-400">No location information provided</div>
                )}
              </div>
            </div>



            {/* Business */}
            <div>
              <h3 className="mb-3 text-lg font-medium text-black">Business</h3>
              <div className="text-sm text-neutral-600">
                {(artist.label || artist.distributor || artist.vevoRep || artist.soundCloudContact) ? (
                  <div className="space-y-1">
                    {artist.label && <div><span className="font-medium">Label:</span> {artist.label}</div>}
                    {artist.distributor && <div><span className="font-medium">Distributor:</span> {artist.distributor}</div>}
                    {artist.vevoRep && <div><span className="font-medium">Vevo Rep:</span> {artist.vevoRep === 'Other' && artist.vevoRepOther ? artist.vevoRepOther : artist.vevoRep}</div>}
                    {artist.soundCloudContact && <div><span className="font-medium">SoundCloud Contact:</span> {artist.soundCloudContact}</div>}
                  </div>
                ) : (
                  <div className="text-neutral-400">No business information provided</div>
                )}
              </div>
            </div>

            {/* Streaming URLs */}
            <div className="min-w-0">
              <h3 className="mb-3 text-lg font-medium text-black">Streaming URLs</h3>
              {/*
                Streaming links are long, unbroken strings with no spaces to wrap
                at, so they need `break-all` to stay inside the card — without it
                a single URL runs past the panel edge and off the page. `min-w-0`
                on the wrappers lets them shrink below their content width, which
                a grid/flex child will not do by default.
              */}
              <div className="space-y-2 text-sm">
                {artist.soundcloudURL && (
                  <div className="min-w-0"><span className="font-medium">SoundCloud:</span>
                    <a href={artist.soundcloudURL} target="_blank" rel="noopener noreferrer" className="ml-1 break-all text-primary-700 hover:text-primary-700">
                      {artist.soundcloudURL}
                    </a>
                  </div>
                )}
                {artist.tidalURL && (
                  <div className="min-w-0"><span className="font-medium">TIDAL:</span>
                    <a href={artist.tidalURL} target="_blank" rel="noopener noreferrer" className="ml-1 break-all text-primary-700 hover:text-primary-700">
                      {artist.tidalURL}
                    </a>
                  </div>
                )}
                {artist.spotifyURL && (
                  <div className="min-w-0"><span className="font-medium">Spotify:</span>
                    <a href={artist.spotifyURL} target="_blank" rel="noopener noreferrer" className="ml-1 break-all text-primary-700 hover:text-primary-700">
                      {artist.spotifyURL}
                    </a>
                  </div>
                )}
                {artist.appleURL && (
                  <div className="min-w-0"><span className="font-medium">Apple:</span>
                    <a href={artist.appleURL} target="_blank" rel="noopener noreferrer" className="ml-1 break-all text-primary-700 hover:text-primary-700">
                      {artist.appleURL}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={() => setIsEditing(true)}>
            Edit
          </Button>
        </div>
      </div>
    );
  }

  // Edit Mode with EXACT same UI layout as AddArtist
  return (
    <div className="bg-white rounded-lg shadow">
      <div className="px-6 py-4 border-b border-neutral-200">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-black">Edit Artist</h1>
          <button
            onClick={handleCancel}
            className="text-neutral-600 hover:text-black"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched }) => (
          <Form className="p-6 space-y-6">
            {/* Photo Upload */}
            <div>
              <label className="block mb-2 text-sm font-medium text-neutral-700">
                Artist Photo
              </label>
              <PhotoUpload
                onPhotoSelect={handlePhotoSelect}
                onAssetSelect={handleAssetSelect}
                preview={photoPreview}
                artistId={artist.id}
              />
            </div>

            {/* Required Fields */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-black">Required Information</h3>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <InputField
                  name="artistName"
                  label="Artist Name"
                  placeholder="Enter Artist Name"
                  required
                />

                <InputField
                  name="spotifyURI"
                  label="Spotify URI"
                  placeholder="25BObiRSDCMwVrBGIVaLIf"
                  required
                  disabled
                />

                <InputField
                  name="appleID"
                  label="Apple ID"
                  placeholder="123456789"
                  required
                />

                <InputField
                  name="asin"
                  label="ASIN"
                  placeholder="B07XVQZXYZ"
                  required
                />

                <InputField
                  name="soundcloudURL"
                  label="SoundCloud URL"
                  placeholder="https://soundcloud.com/artist-name"
                  type="url"
                  required
                />

                <InputField
                  name="tidalURL"
                  label="TIDAL URL"
                  placeholder="https://tidal.com/artist/artist-name"
                  type="url"
                  required
                />
              </div>
            </div>

            {/* Optional Fields */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-black">Optional Information</h3>

              <InputField
                name="bio"
                label="Bio"
                placeholder="Enter artist bio..."
                type="textarea"
                maxLength={5000}
                rows={4}
              />

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <InputField
                  name="hometown"
                  label="Artist Hometown"
                  placeholder="Enter hometown"
                  maxLength={100}
                />

                <div>
                  <label className="block mb-2 text-sm font-medium text-neutral-700">
                    Lyric Language
                  </label>
                  <Field name="lyricLanguage">
                    {({ field, form, meta }: any) => (
                      <>
                        <MultiSelect
                          options={dspDropdownOptions.language}
                          value={field.value ? [field.value] : []}
                          onChange={(value) => {
                            const stringValue = value && value.length > 0 ? value[0] : '';
                            form.setFieldValue("lyricLanguage", stringValue);
                          }}
                          placeholder="Select Language"
                          maxSelections={1}
                        />
                        <ErrorMessage name="lyricLanguage" component="div" className="mt-1 text-sm text-danger-500" />
                      </>
                    )}
                  </Field>
                </div>

                <InputField
                  name="currentTown"
                  label="Current Town"
                  placeholder="Enter current town"
                  maxLength={100}
                />

                <InputField
                  name="country"
                  label="Country"
                  placeholder="Enter country"
                  maxLength={100}
                />

                <div>
                  <label className="block mb-2 text-sm font-medium text-neutral-700">
                    Location of Listeners <span className="text-danger-500">*</span>
                  </label>
                  <Field name="listenersLocation">
                    {({ field, form, meta }: any) => (
                      <>
                        <MultiSelect
                          options={dspDropdownOptions.listenersLocation}
                          value={field.value ? [field.value] : []} // Convert string to array for MultiSelect
                          onChange={(value) => {
                            // Convert array back to string (take first selected value)
                            const stringValue = value && value.length > 0 ? value[0] : '';
                            form.setFieldValue("listenersLocation", stringValue);
                          }}
                          placeholder="Select Location"
                          maxSelections={1}
                          required={true}
                        />
                        <ErrorMessage name="listenersLocation" component="div" className="mt-1 text-sm text-danger-500" />
                      </>
                    )}
                  </Field>
                </div>

                <InputField
                  name="label"
                  label="Label"
                  placeholder="Enter label"
                  maxLength={100}
                />

                <InputField
                  name="distributor"
                  label="Distributor"
                  placeholder="Enter distributor"
                  maxLength={100}
                />
                <div>
                  <label className="block mb-2 text-sm font-medium text-neutral-700">
                    Vevo Rep
                  </label>
                  <Field name="vevoRep">
                    {({ field, form, meta }: any) => (
                      <>
                        <div className="relative">
                          <select
                            {...field}
                            className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${meta.touched && meta.error ? 'border-danger-500' : 'border-neutral-300'}`}
                            onChange={(e) => form.setFieldValue('vevoRep', e.target.value)}
                          >
                            <option value="">Select Vevo Rep</option>
                            {dspDropdownOptions.vevoRep.map((rep: string) => (
                              <option key={rep} value={rep}>{rep}</option>
                            ))}
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <span className="text-xs text-neutral-400">▼</span>
                          </div>
                        </div>
                        <ErrorMessage name="vevoRep" component="div" className="text-sm text-danger-500" />
                      </>
                    )}
                  </Field>
                </div>
                {values.vevoRep === 'Other' && (
                  <InputField
                    name="vevoRepOther"
                    label="Specify Vevo Rep Name"
                    placeholder="Enter Vevo Rep name"
                  />
                )}
                <div>
                  <label className="block mb-2 text-sm font-medium text-neutral-700">
                    SoundCloud Contact
                  </label>
                  <Field name="soundCloudContact">
                    {({ field, form, meta }: any) => (
                      <>
                        <div className="relative">
                          <select
                            {...field}
                            className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:border-primary-500 text-neutral-700 appearance-none pr-10 ${meta.touched && meta.error ? 'border-danger-500' : 'border-neutral-300'}`}
                            onChange={(e) => form.setFieldValue('soundCloudContact', e.target.value)}
                          >
                            <option value="">Select Contact</option>
                            {dspDropdownOptions.soundCloudContact.map((contact: string) => (
                              <option key={contact} value={contact}>{contact}</option>
                            ))}
                          </select>
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                            <span className="text-xs text-neutral-400">▼</span>
                          </div>
                        </div>
                        <ErrorMessage name="soundCloudContact" component="div" className="text-sm text-danger-500" />
                      </>
                    )}
                  </Field>
                </div>
              </div>
              <div>
                <label className="block mb-2 text-sm font-medium text-neutral-700">
                  Similar Artists
                </label>
                <Field name="similarArtists">
                  {({ field, form }: any) => {
                    const currentArtists: string[] = field.value
                      ? field.value.split(',').map((a: string) => a.trim()).filter((a: string) => a)
                      : [];
                    const isMaxReached = currentArtists.length >= 3;
                    const [inputVal, setInputVal] = React.useState('');
                    const addArtist = () => {
                      const newArtist = inputVal.trim();
                      if (newArtist && !currentArtists.includes(newArtist) && currentArtists.length < 3) {
                        form.setFieldValue('similarArtists', [...currentArtists, newArtist].join(', '));
                        setInputVal('');
                      }
                    };
                    return (
                      <div className="w-full bg-white border rounded-md border-neutral-300 focus-within:border-primary-500">
                        {currentArtists.length > 0 && (
                          <div className="flex flex-wrap gap-2 p-3 border-b border-neutral-100">
                            {currentArtists.map((artist: string, index: number) => (
                              <span key={index} className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-900 bg-primary-100 rounded-full gap-2">
                                <span>{artist}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const updated = [...currentArtists];
                                    updated.splice(index, 1);
                                    form.setFieldValue('similarArtists', updated.join(', '));
                                  }}
                                  className="flex items-center justify-center w-4 h-4 text-primary-700 transition-colors rounded-full hover:bg-primary-200 hover:text-primary-900"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="relative flex items-center">
                          <input
                            type="text"
                            value={inputVal}
                            onChange={(e) => setInputVal(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addArtist(); } }}
                            placeholder={isMaxReached ? 'Maximum 3 artists selected' : 'Type artist name and press Enter'}
                            disabled={isMaxReached}
                            className="flex-1 px-3 py-2 text-sm text-neutral-700 placeholder-neutral-400 bg-transparent border-none outline-none disabled:text-neutral-400 disabled:cursor-not-allowed"
                          />
                          {!isMaxReached && inputVal.trim() && (
                            <button type="button" onClick={addArtist} className="flex items-center justify-center w-8 h-8 mr-2 text-primary-700 transition-colors rounded-full hover:bg-primary-50">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  }}
                </Field>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-neutral-500">
                    {(values.similarArtists ? values.similarArtists.split(',').map((a: string) => a.trim()).filter((a: string) => a) : []).length}/3 artists selected
                  </span>
                </div>
              </div>

              {/* Social Media */}
              <div>
                <h4 className="mb-3 font-medium text-black text-md">Social Media</h4>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  <InputField
                    name="instagram"
                    label="Instagram"
                    placeholder="https://instagram.com/username"
                    type="url"
                  />

                  <InputField
                    name="tiktok"
                    label="TikTok"
                    placeholder="https://tiktok.com/@username"
                    type="url"
                  />

                  <InputField
                    name="twitter"
                    label="X"
                    placeholder="https://x.com/username"
                    type="url"
                  />

                  <InputField
                    name="facebook"
                    label="Facebook"
                    placeholder="https://facebook.com/username"
                    type="url"
                  />

                  <InputField
                    name="snapchat"
                    label="Snapchat"
                    placeholder="https://snapchat.com/add/username"
                    type="url"
                  />
                </div>
              </div>

              {/* Streaming URLs */}
              <div>
                <h4 className="mb-3 font-medium text-black text-md">Streaming URLs</h4>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                  <InputField
                    name="spotifyURL"
                    label="Spotify URL"
                    placeholder="https://open.spotify.com/artist/..."
                    type="url"
                  />

                  <InputField
                    name="appleURL"
                    label="Apple URL"
                    placeholder="https://music.apple.com/artist/..."
                    type="url"
                  />

                  <InputField
                    name="amazonURL"
                    label="Amazon URL"
                    placeholder="https://amazon.com/artist/..."
                    type="url"
                  />

                  <InputField
                    name="deezerURL"
                    label="Deezer URL"
                    placeholder="https://deezer.com/artist/..."
                    type="url"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <Button type="button" variant="neutral" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={loading}
                onClick={() => {
                  // Auto-scroll to first error field like in AddArtist.
                  // Matches error styling by class name, so it lists both the
                  // token names and the raw red ones they replaced — error
                  // fields render from more than one file, and those don't
                  // all migrate at once.
                  setTimeout(() => {
                    const errorElement = document.querySelector(
                      '.text-danger-500, .border-danger-500, .text-red-500, .border-red-500'
                    );
                    if (errorElement) {
                      errorElement.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest',
                      });
                    }
                  }, 200);
                }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ArtistDetailsTab;